import { CellValue, ParsedFile, ParseIssue } from '../types';

import { buildSearchIndex } from './buildSearchIndex';
import { ROW_CAP } from './limits';

// Each line is self-contained JSON, so splitting on newlines is safe here (unlike CSV).
// Headers are the union of top-level keys across all rows, in first-seen order — lines
// may be heterogeneous. Nested objects are left intact for the structured renderer, not
// flattened into dot-notation. A malformed line is a ParseIssue, never a fatal error.
export const parseJsonl = (text: string, totalBytes: number | null): ParsedFile => {
  const lines = text.split(/\r?\n/);
  const rows: Record<string, CellValue>[] = [];
  const errors: ParseIssue[] = [];
  const headerOrder: string[] = [];
  const seenHeaders = new Set<string>();

  let truncated = false;

  for (let i = 0; i < lines.length; i += 1) {
    if (rows.length >= ROW_CAP) {
      truncated = true;
      break;
    }

    const line = lines[i].trim();
    if (line) {
      let parsed: unknown;
      let ok = true;
      try {
        parsed = JSON.parse(line);
      } catch {
        errors.push({ rowIndex: i, message: `Line ${i + 1} is not valid JSON` });
        ok = false;
      }

      if (ok && (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))) {
        // A bare value / array line has no columns of its own — expose it under `value`.
        if (!seenHeaders.has('value')) {
          seenHeaders.add('value');
          headerOrder.push('value');
        }
        rows.push({ value: parsed as CellValue });
      } else if (ok) {
        const record = parsed as Record<string, CellValue>;
        Object.keys(record).forEach(key => {
          if (!seenHeaders.has(key)) {
            seenHeaders.add(key);
            headerOrder.push(key);
          }
        });
        rows.push(record);
      }
    }
  }

  return {
    headers: headerOrder,
    rows,
    searchIndex: buildSearchIndex(rows, headerOrder),
    truncated,
    totalBytes,
    errors,
  };
};
